/**
 * MigrationReport.jsx
 * --------------------
 * One-screen "cutover dashboard" the admin opens during a site migration
 * to see, per user, whether they:
 *   ✅ logged in cleanly,
 *   🔁 requested a password reset then logged in,
 *   ⏳ requested a reset but never came back,
 *   ❌ only tried & failed,
 *   ❓ tried with an unknown email.
 *
 * The component is self-contained — it picks the time window, fetches
 * `/api/admin/migration-report`, and renders the table with action-coloured
 * status pills + per-row context (IP, device, country, account_id, etc.).
 *
 * Times are shown in the operator's chosen timezone offset (default GMT+3,
 * Belarus / Moscow) so the migration team can correlate with their own
 * clock without doing UTC math.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CustomerProfileDrawer from './CustomerProfileDrawer';
import {
  ClipboardList, RefreshCw, Search, Users, CheckCircle2, KeyRound,
  Clock, AlertTriangle, XCircle, Download, ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── helpers ──────────────────────────────────────────────────────────── */

const flag = (code) => {
  if (!code || code.length !== 2) return '';
  const A = 0x1F1E6;
  return String.fromCodePoint(A + code.toUpperCase().charCodeAt(0) - 65)
       + String.fromCodePoint(A + code.toUpperCase().charCodeAt(1) - 65);
};

/** Format ISO-UTC into "DD MMM HH:mm" in the chosen TZ offset (hours). */
const fmtTz = (iso, tzOffsetHours) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    // Shift by offset, then read UTC parts → effectively prints in target TZ.
    const shifted = new Date(d.getTime() + tzOffsetHours * 3600 * 1000);
    const dd = String(shifted.getUTCDate()).padStart(2, '0');
    const mm = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][shifted.getUTCMonth()];
    const hh = String(shifted.getUTCHours()).padStart(2, '0');
    const mi = String(shifted.getUTCMinutes()).padStart(2, '0');
    return `${dd} ${mm} ${hh}:${mi}`;
  } catch (_) { return iso; }
};

/* ── status pill ──────────────────────────────────────────────────────── */

const STATUS_META = {
  logged_in:         { label: '✅ Logged in',           cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  reset_then_logged: { label: '🔁 Reset → Logged in',   cls: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  reset_pending:     { label: '⏳ Reset pending',       cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  failed_only:       { label: '❌ Failed only',         cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  unknown_email:     { label: '❓ Unknown email',       cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.failed_only;
  return (
    <span
      data-testid={`mr-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${m.cls}`}
    >
      {m.label}
    </span>
  );
};

/* ── summary tile ─────────────────────────────────────────────────────── */

const Tile = ({ icon: Icon, label, value, accent, active, onClick, testid }) => {
  const ring = active ? 'ring-2 ring-offset-1 ring-cyan-500' : '';
  const pal = {
    cyan:    'from-cyan-500/15 to-cyan-500/0 border-cyan-200 text-cyan-700',
    emerald: 'from-emerald-500/15 to-emerald-500/0 border-emerald-200 text-emerald-700',
    amber:   'from-amber-500/15 to-amber-500/0 border-amber-200 text-amber-700',
    rose:    'from-rose-500/15 to-rose-500/0 border-rose-200 text-rose-700',
    slate:   'from-slate-500/15 to-slate-500/0 border-slate-200 text-slate-700',
  }[accent || 'cyan'];
  const Cmp = onClick ? 'button' : 'div';
  return (
    <Cmp
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-gradient-to-br ${pal} border rounded-xl p-3 hover:shadow-md transition-all ${ring} ${onClick ? 'cursor-pointer hover:-translate-y-0.5' : ''}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <div className="text-2xl font-extrabold leading-tight">{value}</div>
    </Cmp>
  );
};

/* ── main component ───────────────────────────────────────────────────── */

export default function MigrationReport({ token }) {
  const [profileId, setProfileId] = useState(null);
  const [windowPreset, setWindowPreset] = useState('24h');
  // Custom range — only used when windowPreset === 'custom'
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [tzOffset, setTzOffset] = useState(3); // GMT+3 default for BY/RU/UA

  const [data, setData] = useState({ items: [], summary: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');

  const buildRange = useCallback(() => {
    const now = new Date();
    const fmt = (d) => d.toISOString();
    if (windowPreset === '1h') return { start: fmt(new Date(now.getTime() - 3600e3)), end: fmt(now) };
    if (windowPreset === '6h') return { start: fmt(new Date(now.getTime() - 6*3600e3)), end: fmt(now) };
    if (windowPreset === '24h') return { start: fmt(new Date(now.getTime() - 24*3600e3)), end: fmt(now) };
    if (windowPreset === '7d') return { start: fmt(new Date(now.getTime() - 7*86400e3)), end: fmt(now) };
    if (windowPreset === '30d') return { start: fmt(new Date(now.getTime() - 30*86400e3)), end: fmt(now) };
    if (windowPreset === 'custom') return { start: customStart, end: customEnd };
    return {};
  }, [windowPreset, customStart, customEnd]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { start, end } = buildRange();
      const params = new URLSearchParams();
      if (start) params.set('date_start', start);
      if (end) params.set('date_end', end);
      const r = await fetch(`${API_URL}/api/admin/migration-report?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
      setData({ items: [], summary: null });
    } finally {
      setLoading(false);
    }
  }, [buildRange, token]);

  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s during a live migration so the operator sees
  // new logins / reset requests roll in without manual reload.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const filtered = useMemo(() => {
    let xs = data.items || [];
    if (statusFilter !== 'all') xs = xs.filter(it => it.status === statusFilter);
    const q = searchText.trim().toLowerCase();
    if (q) {
      xs = xs.filter(it =>
        (it.email || '').toLowerCase().includes(q)
        || (it.first_name || '').toLowerCase().includes(q)
        || (it.surname || '').toLowerCase().includes(q)
        || String(it.account_id || '').includes(q)
        || (it.ips || []).some(ip => ip.includes(q))
      );
    }
    return xs;
  }, [data.items, statusFilter, searchText]);

  const exportCsv = () => {
    const cols = [
      'email', 'first_name', 'surname', 'account_id', 'user_id', 'status',
      'success_count', 'wrong_password_count', 'user_not_found_count',
      'rate_limited_count', 'reset_request_count', 'reset_completed_count',
      'last_success_at', 'last_failed_at', 'last_reset_request_at',
      'last_reset_completed_at', 'last_activity_at',
      'last_ip', 'last_country_code', 'last_city',
      'last_device_type', 'last_browser', 'ips', 'countries',
    ];
    const esc = (v) => {
      if (v == null) return '';
      const s = Array.isArray(v) ? v.join('|') : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [cols.join(','), ...filtered.map(it => cols.map(c => esc(it[c])).join(','))];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `migration-report-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = data.summary || {};

  return (
    <section data-testid="migration-report" className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 md:p-5 mb-6">
      {/* ── header bar ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-cyan-600" />
          <h2 className="text-lg md:text-xl font-bold text-slate-800">Migration Cutover Report</h2>
          {loading && <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Window preset chips */}
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
            {['1h','6h','24h','7d','30d','custom'].map(p => (
              <button
                key={p} type="button"
                data-testid={`mr-window-${p}`}
                onClick={() => setWindowPreset(p)}
                className={`px-2.5 py-1 text-xs font-bold rounded-md transition ${windowPreset === p ? 'bg-white shadow text-cyan-700' : 'text-slate-500 hover:text-slate-700'}`}
              >{p.toUpperCase()}</button>
            ))}
          </div>
          {/* Timezone toggle */}
          <select
            value={tzOffset}
            onChange={(e) => setTzOffset(Number(e.target.value))}
            data-testid="mr-tz"
            className="text-xs font-bold border border-slate-200 bg-white rounded-md px-2 py-1"
          >
            {[0,1,2,3,4,5,6,7].map(o => <option key={o} value={o}>GMT+{o}</option>)}
          </select>
          {/* Refresh button */}
          <button
            type="button" onClick={load}
            data-testid="mr-refresh"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-slate-100 hover:bg-slate-200 rounded-md"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <label className="inline-flex items-center gap-1 text-xs text-slate-500">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} data-testid="mr-autorefresh" />
            auto
          </label>
          <button
            type="button" onClick={exportCsv}
            data-testid="mr-export-csv"
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md"
          >
            <Download className="w-3 h-3" /> CSV
          </button>
        </div>
      </div>

      {/* Custom range picker */}
      {windowPreset === 'custom' && (
        <div className="flex flex-wrap gap-2 mb-4">
          <input type="datetime-local" value={customStart} onChange={(e)=>setCustomStart(e.target.value)} data-testid="mr-custom-start" className="px-2 py-1 border border-slate-300 rounded text-sm" />
          <span className="text-slate-400 self-center">→</span>
          <input type="datetime-local" value={customEnd} onChange={(e)=>setCustomEnd(e.target.value)} data-testid="mr-custom-end" className="px-2 py-1 border border-slate-300 rounded text-sm" />
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg text-sm font-bold">
          {error}
        </div>
      )}

      {/* ── summary tiles ────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-5">
        <Tile icon={Users}         testid="mr-tile-all"            label="Total users"      value={s.total_users ?? '—'} accent="cyan"
              active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} />
        <Tile icon={CheckCircle2}  testid="mr-tile-logged"         label="Logged in"        value={s.logged_in ?? 0} accent="emerald"
              active={statusFilter === 'logged_in'} onClick={() => setStatusFilter('logged_in')} />
        <Tile icon={KeyRound}      testid="mr-tile-reset-logged"   label="Reset → Logged"   value={s.reset_then_logged ?? 0} accent="cyan"
              active={statusFilter === 'reset_then_logged'} onClick={() => setStatusFilter('reset_then_logged')} />
        <Tile icon={Clock}         testid="mr-tile-reset-pending"  label="Reset pending"    value={s.reset_pending ?? 0} accent="amber"
              active={statusFilter === 'reset_pending'} onClick={() => setStatusFilter('reset_pending')} />
        <Tile icon={XCircle}       testid="mr-tile-failed"         label="Failed only"      value={s.failed_only ?? 0} accent="rose"
              active={statusFilter === 'failed_only'} onClick={() => setStatusFilter('failed_only')} />
        <Tile icon={AlertTriangle} testid="mr-tile-unknown"        label="Unknown email"    value={s.unknown_email ?? 0} accent="slate"
              active={statusFilter === 'unknown_email'} onClick={() => setStatusFilter('unknown_email')} />
      </div>

      {/* Free-text search */}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="search"
          placeholder="Search by email, name, account_id, IP…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          data-testid="mr-search"
          className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
        />
      </div>

      {/* ── table ────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto -mx-1 px-1" data-testid="mr-table-wrap">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="text-left font-bold py-2 px-2 whitespace-nowrap">User</th>
              <th className="text-left font-bold py-2 px-2 whitespace-nowrap">Status</th>
              <th className="text-right font-bold py-2 px-2 whitespace-nowrap">✅ OK</th>
              <th className="text-right font-bold py-2 px-2 whitespace-nowrap">❌ Wrong pw</th>
              <th className="text-right font-bold py-2 px-2 whitespace-nowrap">🔁 Reset</th>
              <th className="text-left font-bold py-2 px-2 whitespace-nowrap">Last activity (GMT+{tzOffset})</th>
              <th className="text-left font-bold py-2 px-2 whitespace-nowrap">Last IP / Geo</th>
              <th className="text-left font-bold py-2 px-2 whitespace-nowrap">Device</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-8">No activity in this window.</td></tr>
            )}
            {filtered.map((it) => (
              <tr key={it.email || it.user_id} data-testid={`mr-row-${it.email}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="py-2 px-2 max-w-[220px]">
                  {it.user_id ? (
                    <button
                      type="button"
                      onClick={() => setProfileId(it.user_id)}
                      data-testid={`mr-open-profile-${it.email}`}
                      title={`Open full profile — ${[it.first_name, it.surname].filter(Boolean).join(' ') || it.email}`}
                      className="font-bold text-cyan-700 hover:text-cyan-900 hover:underline truncate block max-w-full text-left"
                    >
                      {[it.first_name, it.surname].filter(Boolean).join(' ') || it.email}
                    </button>
                  ) : (
                    <div className="font-bold text-slate-800 truncate" title={[it.first_name, it.surname].filter(Boolean).join(' ') || ''}>
                      {[it.first_name, it.surname].filter(Boolean).join(' ') || <span className="text-slate-400 italic">(no name)</span>}
                    </div>
                  )}
                  <div className="text-xs text-slate-500 truncate" title={it.email}>{it.email}</div>
                  {it.account_id != null && (
                    <div className="text-[11px] text-slate-400 font-mono">#{it.account_id}</div>
                  )}
                </td>
                <td className="py-2 px-2 whitespace-nowrap"><StatusPill status={it.status} /></td>
                <td className="py-2 px-2 text-right font-mono whitespace-nowrap tabular-nums">
                  <span className={it.success_count ? 'text-emerald-700 font-bold' : 'text-slate-300'}>{it.success_count}</span>
                </td>
                <td className="py-2 px-2 text-right font-mono whitespace-nowrap tabular-nums">
                  <span className={it.wrong_password_count ? 'text-rose-700 font-bold' : 'text-slate-300'}>{it.wrong_password_count}</span>
                  {it.rate_limited_count > 0 && <span className="text-[10px] text-amber-700 ml-1" title="Rate-limited">+{it.rate_limited_count}🔒</span>}
                  {it.user_not_found_count > 0 && <span className="text-[10px] text-slate-500 ml-1" title="User-not-found attempts">+{it.user_not_found_count}?</span>}
                </td>
                <td className="py-2 px-2 text-right font-mono whitespace-nowrap tabular-nums">
                  {it.reset_request_count > 0 ? (
                    <span className="text-amber-700 font-bold">
                      {it.reset_request_count}
                      {it.reset_completed_count > 0 && (
                        <span className="text-emerald-700 ml-0.5" title="Reset completed">✓{it.reset_completed_count}</span>
                      )}
                    </span>
                  ) : <span className="text-slate-300">0</span>}
                </td>
                <td className="py-2 px-2 text-xs whitespace-nowrap">
                  {it.last_success_at && (
                    <div className="text-emerald-700">✓ {fmtTz(it.last_success_at, tzOffset)}</div>
                  )}
                  {it.last_failed_at && (
                    <div className="text-rose-700">✗ {fmtTz(it.last_failed_at, tzOffset)}</div>
                  )}
                  {it.last_reset_request_at && (
                    <div className="text-amber-700">🔁 {fmtTz(it.last_reset_request_at, tzOffset)}</div>
                  )}
                </td>
                <td className="py-2 px-2 text-xs">
                  {it.last_ip ? (
                    <>
                      <a href={`https://www.iplocation.net/ip-lookup?query=${encodeURIComponent(it.last_ip)}`}
                         target="_blank" rel="noreferrer"
                         className="font-mono text-slate-700 hover:text-cyan-600 inline-flex items-center gap-0.5">
                        {it.last_ip} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                      <div className="text-slate-500">
                        {flag(it.last_country_code)} {it.last_city || it.last_country || ''}
                      </div>
                      {it.ips.length > 1 && (
                        <div className="text-[10px] text-slate-400">+{it.ips.length - 1} more IP{it.ips.length > 2 ? 's' : ''}</div>
                      )}
                    </>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="py-2 px-2 text-xs">
                  <div className="capitalize text-slate-700">{it.last_device_type || '—'}</div>
                  <div className="text-slate-500">{it.last_browser || ''}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span>
          Showing <strong>{filtered.length}</strong> of {data.items?.length || 0} users
          {' · '}Window: <span className="font-mono">{s.window_start ? fmtTz(s.window_start, tzOffset) : '—'}</span>
          {' → '}<span className="font-mono">{s.window_end ? fmtTz(s.window_end, tzOffset) : 'now'}</span>
        </span>
        <span>
          Total: ✅{s.total_success_events ?? 0} OK · ❌{s.total_failed_events ?? 0} failed · 🔁{s.total_reset_requests ?? 0} reset reqs · ✓{s.total_reset_completed ?? 0} reset done
        </span>
      </div>

      {profileId && (
        <CustomerProfileDrawer
          token={token}
          customerId={profileId}
          onClose={() => setProfileId(null)}
        />
      )}
    </section>
  );
}
